import {
  directoryFilterFromQuery,
  directoryFilterSchema,
  type RecordFilterGroup,
} from "@gtm/contracts/core/record-filter";
import { useQuery } from "@tanstack/react-query";
import {
  type FilterField,
  RecordFilterBuilder,
} from "@/components/workspace/record-filter-builder";
import { reviewStatusOptions } from "@/components/workspace/review-status";
import { useTags } from "@/features/tags/tag-controls";
import { apiClient, unwrap } from "@/lib/api-client";

export function DirectoryFilterBuilder({
  kind,
  query,
  onApply,
}: {
  kind: "person" | "company";
  query: Record<string, string | undefined>;
  onApply: (values: Record<string, string>) => void;
}) {
  const tags = useTags();
  const users = useQuery({
    queryKey: ["assignees"],
    queryFn: ({ signal }) => unwrap(apiClient().GET("/organization/assignees", { signal })),
    retry: false,
  });
  const companies = useQuery({
    queryKey: ["company-options"],
    enabled: kind === "person",
    queryFn: ({ signal }) => unwrap(apiClient().GET("/companies/options", { signal })),
    retry: false,
  });
  let applied: RecordFilterGroup = { operator: "and", rules: [] };
  let invalid = false;
  try {
    applied = directoryFilterFromQuery(kind, query);
  } catch {
    invalid = true;
  }
  const fields: FilterField[] =
    kind === "person"
      ? [
          { value: "status", label: "Status" },
          { value: "company", label: "Company" },
        ]
      : [{ value: "domain", label: "Domain" }];
  fields.push(
    { value: "tags", label: "Tags" },
    { value: "assignee", label: "Assignee" },
    { value: "date", label: "Discovered" },
    { value: "text", label: "Text" },
  );
  return (
    <RecordFilterBuilder
      kind={kind}
      applied={applied}
      invalid={invalid}
      fields={fields}
      choices={{
        status: reviewStatusOptions,
        company: (companies.data ?? []).map((company) => ({
          value: company.id,
          label: company.name,
        })),
        tags: (tags.data ?? []).map((tag) => ({ value: tag.id, label: tag.name })),
        assignee: [
          { value: "unassigned", label: "Unassigned" },
          ...(users.data ?? []).map((user) => ({ value: user.id, label: user.name })),
        ],
      }}
      optionsLoading={
        tags.isPending || users.isPending || (kind === "person" && companies.isPending)
      }
      optionsError={!!tags.error || !!users.error || (kind === "person" && !!companies.error)}
      onRetry={() => {
        void tags.refetch();
        void users.refetch();
        if (kind === "person") void companies.refetch();
      }}
      validate={(group) => directoryFilterSchema(kind).safeParse(group).success}
      onApply={(group) =>
        onApply({
          filter: group.rules.length
            ? JSON.stringify(directoryFilterSchema(kind).parse(group))
            : "",
          state: "",
          company: "",
          tags: "",
          domain: "",
          assignee: "",
        })
      }
    />
  );
}
